/**
 * AppPermissionsFilters: the App inventory toolbar (DESIGN.md "Data table",
 * mockup 03): a Carbon search field and a filter menu with one group each for
 * app type, activity, publisher and review status. Each group holds one
 * choice at a time, as the old selects did. The risk tier is the table's
 * contained tabs (AppPermissionsSummaryCards), so it is not repeated here.
 *
 * `AppPermissionsFilters` renders the toolbar controls (pass it to the
 * DataTable `toolbar`); `AppPermissionsFilterTags` renders the active filters
 * as dismissible Carbon filter tags with "Clear filters" (pass it to the
 * DataTable `filterTags`). Both take the same props, which are unchanged. The
 * default "Non-Microsoft" app type gets no tag (the table's notice names it).
 */

import { Button } from '@/components/carbon/Button'
import { TableFilterMenu, type FilterGroup } from '@/components/carbon/TableFilterMenu'
import { TableSearch } from '@/components/carbon/TableSearch'
import { Tag } from '@/components/carbon/Tag'
import type { AcknowledgmentFilterStatus } from './appPermissionsTypes'

interface AppPermissionsFiltersProps {
  riskTier: string | undefined
  appType: string | undefined
  status: string | undefined
  verified: string | undefined
  acknowledgmentStatus: AcknowledgmentFilterStatus | undefined
  search: string
  onRiskTierChange: (v: string | undefined) => void
  onAppTypeChange: (v: string | undefined) => void
  onStatusChange: (v: string | undefined) => void
  onVerifiedChange: (v: string | undefined) => void
  onAcknowledgmentStatusChange: (v: AcknowledgmentFilterStatus | undefined) => void
  onSearchChange: (v: string) => void
  onClearAll: () => void
}

const APP_TYPE_OPTIONS = [
  { value: 'NonMicrosoft', label: 'Non-Microsoft' },
  { value: 'InHouse', label: 'In-house' },
  { value: 'ThirdParty', label: 'Third-party' },
  { value: 'Microsoft', label: 'Microsoft' },
]

const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Stale', label: 'Stale' },
  { value: 'Unknown', label: 'Unknown' },
  { value: 'Disabled', label: 'Disabled' },
]

const PUBLISHER_OPTIONS = [
  { value: 'Verified', label: 'Verified' },
  { value: 'Unverified', label: 'Unverified' },
]

const REVIEW_OPTIONS: { value: AcknowledgmentFilterStatus; label: string }[] = [
  { value: 'Unreviewed', label: 'Not reviewed' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Investigating', label: 'Investigating' },
  { value: 'Flagged', label: 'Flagged' },
]

const labelOf = (options: { value: string; label: string }[], value: string) =>
  options.find(o => o.value === value)?.label ?? value

/**
 * A checkbox group that holds one value: checking an option replaces the
 * current one, and unchecking it clears the filter.
 */
function single<T extends string>(
  legend: string,
  options: { value: T; label: string }[],
  current: T | undefined,
  set: (v: T | undefined) => void,
): FilterGroup {
  return {
    legend,
    options,
    selected: current ? [current] : [],
    onChange: next => set((next.find(v => v !== current) as T | undefined) ?? undefined),
  }
}

export function AppPermissionsFilters({
  appType,
  status,
  verified,
  acknowledgmentStatus,
  search,
  onAppTypeChange,
  onStatusChange,
  onVerifiedChange,
  onAcknowledgmentStatusChange,
  onSearchChange,
}: AppPermissionsFiltersProps) {
  const groups: FilterGroup[] = [
    single('App type', APP_TYPE_OPTIONS, appType, onAppTypeChange),
    single('Activity', STATUS_OPTIONS, status, onStatusChange),
    single('Publisher', PUBLISHER_OPTIONS, verified, onVerifiedChange),
    single('Review status', REVIEW_OPTIONS, acknowledgmentStatus, onAcknowledgmentStatusChange),
  ]

  return (
    <>
      <TableSearch value={search} onChange={onSearchChange} label="Search apps" />
      <TableFilterMenu
        label="Filter apps"
        groups={groups}
        onReset={() => {
          onAppTypeChange(undefined)
          onStatusChange(undefined)
          onVerifiedChange(undefined)
          onAcknowledgmentStatusChange(undefined)
        }}
      />
    </>
  )
}

/** The active filters as Carbon filter tags (a close button each), then "Clear filters", for the DataTable's `filterTags` group. Renders nothing when no filter is on. */
export function AppPermissionsFilterTags({
  riskTier,
  appType,
  status,
  verified,
  acknowledgmentStatus,
  search,
  onRiskTierChange,
  onAppTypeChange,
  onStatusChange,
  onVerifiedChange,
  onAcknowledgmentStatusChange,
  onSearchChange,
  onClearAll,
}: AppPermissionsFiltersProps) {
  const active: { label: string; onRemove: () => void }[] = []
  if (riskTier) active.push({ label: `Risk: ${riskTier}`, onRemove: () => onRiskTierChange(undefined) })
  // "Non-Microsoft" is the inventory's default view; the table's hidden-apps notice names it, so it is not a tag.
  if (appType && appType !== 'NonMicrosoft') {
    active.push({ label: `Apps: ${labelOf(APP_TYPE_OPTIONS, appType)}`, onRemove: () => onAppTypeChange(undefined) })
  }
  if (status) active.push({ label: `Activity: ${labelOf(STATUS_OPTIONS, status)}`, onRemove: () => onStatusChange(undefined) })
  if (verified) active.push({ label: `Publisher: ${labelOf(PUBLISHER_OPTIONS, verified)}`, onRemove: () => onVerifiedChange(undefined) })
  if (acknowledgmentStatus) {
    active.push({
      label: `Review: ${labelOf(REVIEW_OPTIONS, acknowledgmentStatus)}`,
      onRemove: () => onAcknowledgmentStatusChange(undefined),
    })
  }
  if (search) active.push({ label: `Search: ${search}`, onRemove: () => onSearchChange('') })

  if (active.length === 0) return null

  return (
    <>
      {active.map(f => (
        <Tag key={f.label} onDismiss={f.onRemove} dismissLabel={`Remove filter ${f.label}`}>
          {f.label}
        </Tag>
      ))}
      <Button kind="ghost" size="sm" onClick={onClearAll}>
        Clear filters
      </Button>
    </>
  )
}
